// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SectionContainer, SectionTitle } from "@conklin/App/Widgets/DesignSystem/DesignSystem.helpers";
import typography, { fontConfig } from "@conklin/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface TypographyTabStyles {
    titleGridItem?: GridItemProps;
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    formWrapper?: InjectableCss;
}

const typogrphyTabStyles: TypographyTabStyles = {
    titleGridItem: {
        width: 12,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-top: 20px;
            flex-wrap: wrap;
        `,
    },
};

const styles = typogrphyTabStyles;
const TypographyTab: FC = props => {
    return (
        <SectionContainer>
            <GridItem {...styles.titleGridItem}>
                <SectionTitle>{translate("Hierarchy")}</SectionTitle>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Typography variant="h1" size={36} lineHeight="44px" weight={fontConfig.weight.black} color="text">
                    {translate(`Display – Lato : 900 : Font-Size 36px : Line-Height 44px`)}
                </Typography>
                <Typography variant="h1" size={24} lineHeight="29px" weight={fontConfig.weight.black} color="text">
                    {translate(`Display : Mobile + Tablet – Lato : 900 : Font-Size 24px : Line-Height 29px`)}
                </Typography>
                <Typography variant="h1">
                    {translate(
                        `H1 – Lato : [${typography["h1"].weight}] : Font-Size [${typography["h1"].size}] : Line-Height [${typography["h1"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h2">
                    {translate(
                        `H2 – Lato : [${typography["h2"].weight}] : Font-Size [${typography["h2"].size}] : Line-Height [${typography["h2"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h3">
                    {translate(
                        `H3 – Lato : [${typography["h3"].weight}] : Font-Size [${typography["h3"].size}] : Line-Height [${typography["h3"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h4">
                    {translate(
                        `H4 – Lato : [${typography["h4"].weight}] : Font-Size [${typography["h4"].size}] : Line-Height [${typography["h4"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h5">
                    {translate(
                        `H5 – Lato : [${typography["h5"].weight}] : Font-Size [${typography["h5"].size}] : Line-Height [${typography["h5"].lineHeight}]`,
                    )}
                </Typography>
                <Typography variant="h6">
                    {translate(
                        `H6 – Lato : [${typography["h6"].weight}] : Font-Size [${typography["h6"].size}] : Line-Height [${typography["h6"].lineHeight}]`,
                    )}
                </Typography>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Body Copy : Regular</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Typography variant="p">Lato : 400 : Font-Size 16px : Line-Height: 26px</Typography>
                    <Typography variant="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat. Lorem ipsum dolor sit amet, consectetur
                        adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad
                        minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                    <Typography variant="p">
                        Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed do eiusmod tempor incididunt ut
                        labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
                        laboris nisi ut aliquip ex ea commodo consequat. Lorem ipsum dolor sit amet, consectetur
                        adipisicing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad
                        minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </Typography>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.gridItem}>
                <SectionTitle>Links</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <Link href="https://www.google.com/">Text Link</Link>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Bulleted Lists</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <UnorderedList>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>
                            Unordered List Item w/ Sub-List
                            <UnorderedList>
                                <ListItem>Unordered Sub-List Item</ListItem>
                                <ListItem>Unordered Sub-List Item</ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>Unordered List Item</ListItem>
                        <ListItem>Unordered List Item</ListItem>
                    </UnorderedList>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.smallGridItem}>
                <SectionTitle>Numbered List</SectionTitle>
                <StyledWrapper {...styles.formWrapper}>
                    <OrderedList>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>
                            Numbered List Item w/ Sub-List
                            <OrderedList>
                                <ListItem>Numbered Sub-List Item</ListItem>
                                <ListItem>Numbered Sub-List Item</ListItem>
                            </OrderedList>
                        </ListItem>
                        <ListItem>Numbered List Item</ListItem>
                        <ListItem>Numbered List Item</ListItem>
                    </OrderedList>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default TypographyTab;
